import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { getDossier } from "../../api/dossier/getDossier";
import { getDossierVehicleReview } from "../../api/dossier/getDossierVehicleReview";
import { getVehicleImages } from "../../api/vehicle/getVehicleImages";
import AITermsText from "../../components/AITermsText";
import APButton from "../../components/buttons/APButton";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import OverwriteQuoteModal from "../../components/modals/OverwriteQuoteModal";
import PageContainer from "../../components/PageContainer";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import CopyExternalUrlField from "../../components/utils/CopyExternalUrlField";
import CopyMarketingUrl from "../../components/utils/CopyMarketingUrl";
import { isAIVehicleSuggestionEnabled } from "../../helpers/configuration";
import { buildQuoteQueryString } from "../../helpers/quoteParams";
import { useAIConsent } from "../../hooks/useAIConsent";
import { useFaqs } from "../../hooks/useFaqs";
import { useQuoteLimit } from "../../hooks/useQuoteLimit";
import { useAuth } from "../../providers/AuthProvider";
import APVehicleReview from "./components/VehicleReview";

const DossierVehicleReview = () => {
  const { dossierId, vehicleCode } = useParams();
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();
  const { shouldPrompt: shouldPromptForAIConsent } = useAIConsent();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quoteId") || undefined;
  const hideQuoteButton =
    !!searchParams.get("hideQuoteButton") &&
    searchParams.get("hideQuoteButton") === "true";

  if (!dossierId || !vehicleCode) {
    // TODO: improve client side error messaging
    throw new Error("dossier id or vehicle code param is misisng");
  }

  const review = getDossierVehicleReview({
    dossierId,
    redbookCode: vehicleCode,
  });
  const vehicleImages = getVehicleImages(vehicleCode);

  const dossier = getDossier(dossierId);
  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "vehicle-review",
    contextIds: {
      opportunityId: dossier.data?.opportunity?.id,
    },
  });

  const configMap = getPublicConfig();

  if (dossier.isError) {
    throw dossier.error;
  }

  if (review.isError) {
    throw review.error;
  }

  if (vehicleImages.isError) {
    throw vehicleImages.error;
  }

  if (configMap.isError) {
    throw configMap.error;
  }

  // Use quote limit hook
  const primaryQuoteId = dossier.data?.opportunity?.primaryQuoteId ?? null;
  const { cemakeId, cemodelId, year, cevariantId } = review.data?.review
    .variant ?? {
    cemakeId: null,
    cemodelId: null,
    year: null,
    cevariantId: null,
  };
  const hasCEVehicleIds = !!cemakeId && !!cemodelId && !!year && !!cevariantId;

  const {
    showOverwriteModal,
    handleQuoteAction,
    handleOverwriteConfirm,
    handleOverwriteCancel,
    oldestQuote,
  } = useQuoteLimit({
    dossierId: dossierId || "",
    primaryQuoteId: primaryQuoteId,
    onRedirect: (overwriteQuoteId?: string) => {
      // Build quote parameters using centralized helper
      const quoteParams = {
        makeId: cemakeId,
        modelId: cemodelId,
        modelYear: year,
        variantId: cevariantId,
        overwriteQuote: overwriteQuoteId,
        vehicleReviewAIQueryId: review.data?.queryId,
      };

      const queryString = buildQuoteQueryString(quoteParams);

      if (quoteId) {
        navigate(`${getScopedPageUrl("editQuote", quoteId)}${queryString}`);
      } else if (primaryQuoteId) {
        navigate(
          `${getScopedPageUrl("cloneQuote", primaryQuoteId)}${queryString}`
        );
      } else {
        navigate(`${getScopedPageUrl("createQuote", dossierId)}${queryString}`);
      }
    },
    onCancel: () => {},
  });

  const handleVehicleSuggestions = () => {
    const _quoteId = quoteId || primaryQuoteId;

    if (_quoteId && hasCEVehicleIds) {
      navigate(
        `${getScopedPageUrl(
          "viewQuoteVehicleSuggestions",
          _quoteId
        )}?makeId=${cemakeId}&modelId=${cemodelId}&modelYear=${year}&variantId=${cevariantId}${
          quoteId ? `&editQuote=true` : ""
        }`
      );
    }
  };

  const handleVehicleSpecs = () => {
    const params = new URLSearchParams({
      ...(quoteId && { quoteId }),
      ...(hideQuoteButton && { hideQuoteButton: "true" }),
    });

    const queryString = params.toString();

    navigate(
      `${getScopedPageUrl("dossierVehicleDetail", dossierId, vehicleCode)}${
        queryString ? `?${queryString}` : ""
      }`
    );
  };

  const isLoading =
    review.isLoading ||
    vehicleImages.isLoading ||
    dossier.isLoading ||
    configMap.isLoading ||
    isLoadingFaqs;
  const isSuccess =
    review.isSuccess &&
    vehicleImages.isSuccess &&
    dossier.isSuccess &&
    configMap.isSuccess &&
    isSuccessFaqs;

  const isVehicleSuggestionsEnabled =
    configMap.isSuccess && isAIVehicleSuggestionEnabled(configMap.data);

  return (
    <PageContainer
      loading={isLoading}
      title="AI Vehicle Review"
      titleButton={
        hasCEVehicleIds &&
        !hideQuoteButton && (
          <APButton
            text="Quote"
            color="secondary"
            variant="contained"
            icon="play"
            onClick={() => handleQuoteAction(!!quoteId)}
          />
        )
      }
    >
      {isSuccess && (
        <>
          <OverwriteQuoteModal
            isOpen={showOverwriteModal}
            onClose={handleOverwriteCancel}
            onConfirm={handleOverwriteConfirm}
            oldestQuote={oldestQuote}
          />
          <Stack spacing={2}>
            <RestrictedAppScope scope="internal">
              <Box>
                <CopyExternalUrlField text="Copy the URL below to share this review (this is a driver specific url):" />
              </Box>
            </RestrictedAppScope>

            <APVehicleReview
              review={review.data.review}
              vehicleImages={vehicleImages.data}
              displaySuggestionsButton={
                isVehicleSuggestionsEnabled &&
                !!primaryQuoteId &&
                hasCEVehicleIds
              }
              shouldPromptForAIConsent={shouldPromptForAIConsent}
              onVehicleSuggestions={handleVehicleSuggestions}
              faqProps={faqProps}
            />
          </Stack>
          <Stack
            spacing={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              mt: 2,
              pt: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                columnGap: 1,
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Box
                  sx={{ display: "flex", columnGap: 1, alignItems: "center" }}
                >
                  <BackButton />
                  <APButton
                    text="Specs"
                    variant="contained"
                    onClick={() => handleVehicleSpecs()}
                  />
                </Box>
              </Box>
              {hasCEVehicleIds && !hideQuoteButton && (
                <Box>
                  <APButton
                    text="Quote"
                    color="secondary"
                    variant="contained"
                    icon="play"
                    onClick={() => handleQuoteAction(!!quoteId)}
                  />
                </Box>
              )}
            </Box>
            <Box>
              <RestrictedAppScope scope="external">
                <APContactHelpButton
                  regardingOpportunityId={
                    dossier.data.opportunity?.id ?? undefined
                  }
                />
              </RestrictedAppScope>
            </Box>
            <Box>
              <AITermsText subject="Vehicle Reviews" />
            </Box>
            <CopyMarketingUrl
              type="dossierVehicleReview"
              secondaryId={vehicleCode}
            />
          </Stack>
        </>
      )}
    </PageContainer>
  );
};

export default DossierVehicleReview;
